import type { Contact } from '@/gen/cortado/v1/contacts_pb'
import type { EmailMessageDirection } from '@/gen/cortado/v1/emails_pb'
import type { Inquiry, InquiryLite, InquiryEvent } from '@/gen/cortado/v1/inquiries_pb'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { InquiryStageEnum, InquiryTagEnum } from '@/gen/cortado/v1/settings_pb'
import { getFirstEventDetails } from '@/lib/inquiry-utils'
import { dateToTimestamp } from '@/lib/date-utils'

// LeadLite is a lightweight lead representation for list views
// Based on InquiryLite proto message
export interface LeadLite {
  id: string
  organizationId: string
  stage: InquiryStageEnum
  firstEventDate?: Timestamp
  lastEventDate?: Timestamp
  eventsCount: number
  totalGuestCount: number
  eventType?: string
  contactId: string
  contactFirstName: string
  contactLastName: string
  quoteTotal?: number
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

/**
 * Maps a proto InquiryLite message to a Lead interface
 *
 * This function:
 * - Converts proto InquiryLite to UI-friendly Lead type
 * - Maps stage to status for Kanban compatibility
 * - Creates contact object from first/last name fields
 * - Handles optional fields appropriately
 *
 * @param lite - Proto InquiryLite message from backend
 * @returns Lead object ready for UI consumption
 */
export function mapInquiryLiteToLead(lite: InquiryLite): Lead {
  return {
    id: lite.id,
    organizationId: lite.organizationId,
    contactId: lite.contactId,
    stage: lite.stage,
    status: lite.stage, // For KanbanItem compatibility
    eventType: lite.eventTypeKey,
    firstEventDate: lite.firstEventDate,
    firstEventTimezone: lite.firstEventTimezone,
    lastEventDate: lite.lastEventDate,
    eventsCount: lite.eventsCount,
    totalGuestCount: lite.totalGuestCount,
    events: lite.inquiryEvents,
    quoteTotal: lite.quoteTotal,
    createdAt: lite.createdAt,
    updatedAt: lite.updatedAt,
    contact: {
      id: lite.contactId,
      firstName: lite.contactFirstName,
      lastName: lite.contactLastName,
    } as Contact,
    bookingId: lite.bookingId,
    createdBy: '',
    updatedBy: '',
    tags: lite.tags,
    contactCompanyName: lite.contactCompanyName,
    source: lite.source,
    archivedAt: lite.archivedAt,
    assignedTo: lite.assignedTo,
    assignedToFirstName: lite.assignedToFirstName,
    assignedToLastName: lite.assignedToLastName,
    lastInboundAt: lite.lastInboundAt,
    lastOutboundAt: lite.lastOutboundAt,
    lastMessageDirection: lite.lastMessageDirection,
  }
}

// Lead is based on Inquiry with additional UI-specific fields
// We omit the Message-specific fields like $typeName and $unknown
export interface Lead {
  // Core Inquiry fields
  id: string
  organizationId: string
  contactId: string
  stage: InquiryStageEnum
  eventType?: string
  eventDays?: number
  eventLocation?: string
  referrer?: string
  createdAt?: Timestamp
  createdBy: string
  updatedAt?: Timestamp
  updatedBy: string

  // Event info (derived from first event or lite fields)
  firstEventDate?: Timestamp
  firstEventTimezone?: string
  lastEventDate?: Timestamp
  eventsCount: number
  totalGuestCount: number
  events?: InquiryEvent[]

  // KanbanItem compatibility
  status: number // Mapped from Inquiry.stage for Kanban

  // Additional UI fields
  contact?: Contact // Populated contact data
  company?: string // Additional UI field
  contactCompanyName?: string // Company name from InquiryLite
  quoteTotal?: number
  bookingId?: string // Set if lead has been converted to booking
  tags?: InquiryTagEnum[] // Quick status tags (Follow Up, Urgent, Hot Lead, VIP, Waiting, Reply needed)
  source?: string // JSON-encoded attribution data
  archivedAt?: Timestamp // Set when the lead has been archived (soft-deleted)
  assignedTo?: string // Org user ID the lead is assigned to
  assignedToFirstName?: string
  assignedToLastName?: string

  // Mail on the lead (CPRO-790)
  lastInboundAt?: Timestamp
  lastOutboundAt?: Timestamp
  lastMessageDirection?: EmailMessageDirection
}

/**
 * Maps a proto Inquiry message to a Lead interface
 *
 * This function:
 * - Converts proto Inquiry to UI-friendly Lead type
 * - Maps stage to status for Kanban compatibility
 * - Preserves all enriched fields (contact, quoteTotal)
 * - Extracts first event date for display
 *
 * @param inquiry - Proto Inquiry message from backend
 * @returns Lead object ready for UI consumption
 */
export function mapInquiryToLead(inquiry: Inquiry): Lead {
  // Get first event date and timezone for display (coupled from same source)
  const firstEvent = getFirstEventDetails(inquiry.events)
  const firstEventDate = firstEvent.eventDate ? dateToTimestamp(firstEvent.eventDate) : undefined
  const firstEventTimezone = firstEvent.eventTimezone

  // Calculate total guest count from events
  const totalGuestCount = inquiry.events?.reduce((sum, event) => sum + (event.eventGuests ?? 0), 0) ?? 0

  return {
    id: inquiry.id,
    organizationId: inquiry.organizationId,
    contactId: inquiry.contactId,
    stage: inquiry.stage,
    status: inquiry.stage, // For KanbanItem compatibility
    eventType: inquiry.eventTypeKey,
    eventDays: inquiry.eventDays,
    eventLocation: inquiry.eventLocation,
    referrer: inquiry.referrer,
    createdAt: inquiry.createdAt,
    createdBy: inquiry.createdBy,
    updatedAt: inquiry.updatedAt,
    updatedBy: inquiry.updatedBy,
    contact: inquiry.contact,
    quoteTotal: inquiry.quoteTotal,
    firstEventDate,
    firstEventTimezone,
    eventsCount: inquiry.events?.length ?? 0,
    totalGuestCount,
    events: inquiry.events,
    tags: inquiry.tags,
    source: inquiry.source,
    archivedAt: inquiry.archivedAt,
    assignedTo: inquiry.assignedTo,
    assignedToFirstName: inquiry.assignedToUser?.firstName,
    assignedToLastName: inquiry.assignedToUser?.lastName,
    lastInboundAt: inquiry.lastInboundAt,
    lastOutboundAt: inquiry.lastOutboundAt,
    lastMessageDirection: inquiry.lastMessageDirection,
  }
}
